export type IncomeDisplayBand = {
  label: string;
  memberNames: string[];
};

/** CE catalog type names grouped into the 5 collapsed display bands. */
export const CE_INCOME_DISPLAY_BANDS: IncomeDisplayBand[] = [
  {
    label: "Under $50K",
    memberNames: ["Under $50K", "Under $30K", "$30K - $50K"],
  },
  {
    label: "$50–100K",
    memberNames: ["$50–100K", "$50K - $75K", "$75K - $100K"],
  },
  {
    label: "$100–150K",
    memberNames: ["$100–150K", "$100K - $125K", "$125K - $150K"],
  },
  {
    label: "$150–200K",
    memberNames: ["$150–200K", "$150K - $175K", "$175K - $200K"],
  },
  { label: "$200K+", memberNames: ["$200K+"] },
];

export type DemographicOption = {
  name: string;
  value: number | string;
  codes?: number[];
};

/** Catalog option row; API schema fields are optional and value is unknown. */
export type DemographicOptionLookup = {
  name?: string;
  value?: unknown;
  codes?: number[];
};

export function optionCodes(option: DemographicOption): Array<number | string> {
  if (option.codes?.length) {
    return option.codes.map((code) => Number(code));
  }
  const numeric = Number(option.value);
  return Number.isNaN(numeric) ? [option.value] : [numeric];
}

export function findOptionForValue(
  options: DemographicOptionLookup[] | undefined,
  value: number | string,
): DemographicOptionLookup | undefined {
  if (!options?.length) return undefined;
  const needle = typeof value === "string" ? value : Number(value);
  return options.find((option) => {
    const codes = option.codes?.map(Number) ?? [];
    if (codes.length) {
      return codes.includes(Number(value));
    }
    // A row with no codes and no value can never be a meaningful match, and
    // String(undefined) === "undefined" would otherwise let one slip through.
    if (option.value === undefined) return false;
    return (
      option.value === needle ||
      option.value === value ||
      String(option.value) === String(value)
    );
  });
}

export type GroupedCeIncomeType<
  T extends { id: number | string; name: string },
> = T & {
  members?: T[];
};

export function groupCeIncomeTypes<
  T extends { id: number | string; name: string },
>(items: T[]): Array<GroupedCeIncomeType<T>> {
  const byName = new Map(items.map((item) => [item.name, item]));
  const usedIds = new Set<number | string>();
  const grouped: Array<GroupedCeIncomeType<T>> = [];

  for (const band of CE_INCOME_DISPLAY_BANDS) {
    const members = band.memberNames
      .map((name) => byName.get(name))
      .filter((item): item is T => Boolean(item));
    if (!members.length) continue;
    members.forEach((member) => usedIds.add(member.id));
    grouped.push({
      ...members[0],
      id: `income-band-${band.label}`,
      name: band.label,
      members,
    });
  }

  for (const item of items) {
    if (!usedIds.has(item.id)) {
      grouped.push(item);
    }
  }
  return grouped;
}

export function flattenCeIncomeItems<T extends { members?: T[] }>(
  items: T[],
): T[] {
  return items.flatMap((item) =>
    item.members?.length ? item.members : [item],
  );
}

/** Collapse selected income catalog types into the same bands shown in the picker. */
export function chipsForCeSelections<
  T extends { id: number | string; name: string; type?: string },
>(selected: T[]): Array<GroupedCeIncomeType<T>> {
  const income = selected.filter((item) => item.type === "income_level");
  if (!income.length) return selected;

  const groupedIncome = groupCeIncomeTypes(income);
  const chips: Array<GroupedCeIncomeType<T>> = [];
  let insertedIncome = false;

  for (const item of selected) {
    if (item.type === "income_level") {
      if (!insertedIncome) {
        chips.push(...groupedIncome);
        insertedIncome = true;
      }
      continue;
    }
    chips.push(item);
  }
  return chips;
}
